import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getLabelsets } from '../../services/assetServices';
import {
  createUser,
  editUser,
  getUser,
  toQualificationWrite,
  UserRoleType,
  type IUserQualification,
} from '../../services/userService';
import { IGroup, IGroupInUser, getGroups } from '../../services/groupService';
import { v4 as uuid } from 'uuid';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  makeStyles,
  Skeleton,
  SkeletonItem,
  Text,
  tokens,
} from '@fluentui/react-components';
import { EditItem, useFetch } from '@moko/ui';
import OtherInfo from '../../common/OtherInfoPortal';
import GroupSelector from './GroupSelector';
import CertificationSelector from './CertificationSelector';
import { getTenant } from '../../services/tenantService';
import { getTenantsAdapter } from '../../common/selectors/TenantSelector';

const USER_ROLE_TYPES: Record<string, UserRoleType> = {
  'FMSManager.Access': UserRoleType.FMSManager,
  'FMSClient.Access': UserRoleType.FMSClient,
  'FMSContractor.Access': UserRoleType.FMSContractor,
  'FMSPortal.AdminAccess': UserRoleType.FMSPortalAdmin,
};

const USER_ROLES: { key: string; value: string }[] = [
  {
    key: 'FMSManager.Access',
    value: 'Manager',
  },
  {
    key: 'FMSClient.Access',
    value: 'Client',
  },
  {
    key: 'FMSContractor.Access',
    value: 'Contractor',
  },
  {
    key: 'FMSPortal.AdminAccess',
    value: 'Portal Admin',
  },
];

const roleKeyFromValue = (value: unknown): string | undefined => {
  if (value == null || value === '') return undefined;
  const raw = String(value).trim();
  return USER_ROLES.find((role) => role.key.toLowerCase() === raw.toLowerCase())
    ?.key;
};

const parseRoles = (value: unknown): string[] => {
  const raw = Array.isArray(value)
    ? value
    : typeof value === 'string'
    ? value.split('|')
    : [];
  const roles: string[] = [];
  for (const item of raw) {
    const key = roleKeyFromValue(item);
    if (!key || roles.includes(key)) continue;
    roles.push(key);
  }
  return roles;
};

const rolesToLookupValue = (roles: string[]) => roles.join('|');

const pickDefaultRole = (
  roles: string[],
  value: unknown
): string | undefined => {
  const key = roleKeyFromValue(value);
  if (key && roles.includes(key)) return key;
  return roles[0];
};

const useStyles = makeStyles({
  page: {
    width: '100%',
    maxWidth: '800px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXXL,
  },
  title: {
    display: 'block',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  sectionLabel: {
    display: 'block',
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%',
    justifySelf: 'stretch',
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '14px',
    width: '100%',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'min(12rem, 40%) minmax(0, 1fr)',
    columnGap: '8px',
    alignItems: 'center',
    width: '100%',
  },
  labelBone: {
    justifySelf: 'start',
  },
  valueBone: {
    width: '100%',
    borderRadius: tokens.borderRadiusMedium,
  },
});

const USER_LABEL_WIDTHS = [76, 72, 70, 42, 168, 42, 86];
const OTHER_LABEL_WIDTHS = [52, 68];

const FieldSkeleton = ({
  labelWidths,
  actions = false,
}: {
  labelWidths: number[];
  actions?: boolean;
}) => {
  const styles = useStyles();
  return (
    <Skeleton className={styles.form} aria-label="Loading fields">
      {actions && (
        <div className={styles.actions}>
          <SkeletonItem size={32} style={{ width: 72 }} />
          <SkeletonItem size={32} style={{ width: 84 }} />
        </div>
      )}
      <div className={styles.fields}>
        {labelWidths.map((width, index) => (
          <div key={index} className={styles.fieldRow}>
            <SkeletonItem
              className={styles.labelBone}
              size={16}
              style={{ width }}
            />
            <SkeletonItem className={styles.valueBone} size={32} />
          </div>
        ))}
      </div>
    </Skeleton>
  );
};

const User = () => {
  const isSaved = useRef(false);
  // Provisioning fields only apply when creating a user (no ?id= in the route).
  const isCreate = !new URLSearchParams(useLocation().search).get('id');

  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const [userReady, setUserReady] = useState(isCreate);
  const [pageTitle, setPageTitle] = useState(isCreate ? 'New user' : '');
  const [membership, setMembership] = useState<IGroupInUser[]>([]);
  const [userOtherInfo, setUserOtherInfo] = useState<string>('{}');
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [qualifications, setQualifications] = useState<IUserQualification[]>(
    []
  );
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<
    number | undefined
  >();
  const [selectedTenantName, setSelectedTenantName] = useState<string>();

  const groupsFetch = useFetch({
    queryKey: ['groups', 9999, 1],
    queryFn: (abortController) => getGroups(abortController, 9999, 1),
    onError: (error) => setErrorMessage(error.message),
  });
  const groups = useMemo(() => {
    const byId = new Map<number, IGroup>();
    for (const group of groupsFetch.data?.items ?? [])
      byId.set(group.id, group);
    for (const group of membership) {
      if (!byId.has(group.id)) {
        byId.set(group.id, { id: group.id, name: group.name, users: [] });
      }
    }
    return [...byId.values()];
  }, [groupsFetch.data, membership]);

  const labelSetsFetch = useFetch({
    queryKey: ['labelSets'],
    queryFn: getLabelsets,
    onError: (error) => setErrorMessage(error.message),
  });
  const labelSets = Array.isArray(labelSetsFetch.data)
    ? labelSetsFetch.data
    : [];

  useEffect(() => {
    if (isCreate) setQualifications([]);
  }, [isCreate]);

  const styles = useStyles();

  return (
    <div className={styles.page}>
      {userReady ? (
        <Text className={styles.title} size={500} weight="semibold">
          {pageTitle}
        </Text>
      ) : (
        <Skeleton aria-label="Loading user name">
          <SkeletonItem size={28} style={{ width: 180 }} />
        </Skeleton>
      )}
      <section className={styles.section}>
        <Text className={styles.sectionLabel} size={400} weight="semibold">
          User Info
        </Text>
        <EditItem
          loadingPlaceholder={
            <FieldSkeleton labelWidths={USER_LABEL_WIDTHS} actions />
          }
          getAction={async (abortController, id) => {
            try {
              const user: any = await getUser(abortController, id);
              const roles = parseRoles(user.roles);
              const defaultRole = pickDefaultRole(roles, user.defaultRole);
              setSelectedRoles(roles);
              const membershipGroups = (user.groups ?? []) as IGroupInUser[];
              setSelectedGroupIds(membershipGroups.map((group) => group.id));
              setMembership(membershipGroups);
              setQualifications(
                Array.isArray(user.qualifications) ? user.qualifications : []
              );
              user.roles = rolesToLookupValue(roles);
              user.defaultRole = defaultRole ?? '';
              user.defaultTenant = (user.defaultTenant ?? '').toString();
              const tenantId = user.defaultTenant
                ? Number.parseInt(user.defaultTenant, 10)
                : undefined;
              setSelectedTenantId(tenantId);
              if (tenantId) {
                try {
                  const tenant = await getTenant(abortController, tenantId);
                  setSelectedTenantName(tenant.name);
                } catch {
                  setSelectedTenantName(undefined);
                }
              }
              setUserOtherInfo(user.otherInfo || '{}');
              const username = String(user.username ?? '').trim();
              const fullName = `${user.firstName ?? ''} ${
                user.lastName ?? ''
              }`.trim();
              setPageTitle(username || fullName || 'User');
              return user;
            } catch (error) {
              setPageTitle('User');
              throw error;
            } finally {
              setUserReady(true);
            }
          }}
          newAction={async (abortController, item) => {
            const newUser = item as Record<string, string>;
            const provisionIdentity = newUser.provisionIdentity === 'true';
            const roles = parseRoles(newUser.roles);
            const defaultRole = pickDefaultRole(roles, newUser.defaultRole);
            const newUserResponse = createUser(abortController, {
              username: newUser.username,
              firstName: newUser.firstName,
              lastName: newUser.lastName,
              email: newUser.email,
              groupsId: selectedGroupIds,
              defaultTenantId: Number.parseInt(newUser.defaultTenant, 10) || 0,
              otherInfo: userOtherInfo,
              ...(qualifications.length > 0 && {
                qualifications: qualifications.map(toQualificationWrite),
              }),
              roles: roles.map((role) => USER_ROLE_TYPES[role]),
              defaultRole:
                defaultRole === undefined
                  ? undefined
                  : USER_ROLE_TYPES[defaultRole],
              provisionIdentity,
              isExternalUser:
                provisionIdentity && newUser.isExternalUser === 'true',
              ...(provisionIdentity && {
                identityPassword: newUser.identityPassword,
              }),
            });
            setSuccessMessage(
              `User with id: ${(await newUserResponse).userId} created.`
            );
          }}
          editAction={async (abortController, id, item) => {
            const editedUser = item as Record<string, string>;
            const roles = parseRoles(editedUser.roles);
            const defaultRole = pickDefaultRole(roles, editedUser.defaultRole);
            try {
              await editUser(abortController, id, {
                username: editedUser.username,
                firstName: editedUser.firstName,
                lastName: editedUser.lastName,
                email: editedUser.email,
                groupsId: selectedGroupIds,
                defaultTenantId:
                  Number.parseInt(editedUser.defaultTenant, 10) || 0,
                otherInfo: userOtherInfo,
                roles,
                defaultRole,
                qualifications: qualifications.map(toQualificationWrite),
              });
              setSuccessMessage(`User with id: ${id} successfully updated.`);
            } catch (error: any) {
              setErrorMessage(error.message);
            }
          }}
          isSaved={isSaved}
          afterChange={(next) => {
            setSelectedRoles(parseRoles(next.roles));
          }}
          onFetchTenants={getTenantsAdapter}
          back={'/security?tab=users'}
          defaultValues={JSON.stringify({ uuid: uuid() })}
          metadata={{
            fields: [
              {
                name: 'username',
                fieldType: 'String',
                label: 'User name',
              },
              {
                name: 'firstName',
                fieldType: 'String',
                label: 'First name',
              },
              {
                name: 'lastName',
                fieldType: 'String',
                label: 'Last name',
              },
              {
                name: 'email',
                fieldType: 'String',
                label: 'Email',
              },
              {
                name: 'defaultTenant',
                fieldType: 'TenantSelector',
                label: 'Default tenant (optional)',
                lookupList: 'Tenants',
                mandatory: false,
              },
              {
                name: 'roles',
                fieldType: 'Lookup',
                label: 'Roles',
                multiSelect: true,
                lookupList: 'Roles',
              },
              {
                name: 'defaultRole',
                fieldType: 'Lookup',
                label: 'Default role',
                lookupList: 'DefaultRoles',
              },
              ...(isCreate
                ? [
                    {
                      name: 'provisionIdentity',
                      fieldType: 'Checkbox',
                      label: 'Provision identity',
                    },
                    {
                      name: 'identityPassword',
                      fieldType: 'Password',
                      label: 'Identity password',
                      effect: 'Hide',
                    },
                    {
                      name: 'isExternalUser',
                      fieldType: 'Checkbox',
                      label: 'External user',
                      effect: 'Hide',
                    },
                  ]
                : []),
            ],
            lookups: [
              {
                name: 'Tenants',
                values:
                  selectedTenantId != null && selectedTenantName
                    ? [
                        {
                          key: String(selectedTenantId),
                          value: selectedTenantName,
                        },
                      ]
                    : [],
              },
              {
                name: 'Roles',
                values: USER_ROLES,
              },
              {
                name: 'DefaultRoles',
                values: USER_ROLES.filter((role) =>
                  selectedRoles.includes(role.key)
                ),
              },
            ],
            validations: [],
            interactions: isCreate
              ? [
                  {
                    name: 'showIdentityFields',
                    description:
                      'Reveal identity password and external-user flag only when provisioning an identity.',
                    condition: {
                      combinator: 'And',
                      not: false,
                      rules: [
                        {
                          field: 'provisionIdentity',
                          operator: '=',
                          value: 'true',
                        },
                      ],
                    },
                    effects: [
                      {
                        fields: ['identityPassword', 'isExternalUser'],
                        effectType: 'Show',
                      },
                    ],
                  },
                ]
              : [],
          }}
        />
        <GroupSelector
          groups={groups}
          selectedIds={selectedGroupIds}
          onChange={setSelectedGroupIds}
          label="Groups"
          mandatory
          loading={groupsFetch.loading}
        />
        <CertificationSelector
          qualifications={qualifications}
          onChange={setQualifications}
        />
      </section>
      <section className={styles.section}>
        <Text className={styles.sectionLabel} size={400} weight="semibold">
          Other Info
        </Text>
        {userReady ? (
          <OtherInfo
            metadata={{
              fields: [
                {
                  name: 'mobile',
                  fieldType: 'String',
                  label: 'Mobile',
                },
                {
                  name: 'LabelSet',
                  fieldType: 'Lookup',
                  label: 'Label set',
                  lookupList: 'labelSets',
                },
              ],
              lookups: [
                {
                  name: 'labelSets',
                  values: (labelSets ?? []).map((labelSet) => ({
                    key: labelSet,
                    value: labelSet,
                  })),
                },
              ],
              validations: [],
              interactions: [],
            }}
            isSaved={isSaved}
            onOtherInfoChanged={(newValue: string) => {
              setUserOtherInfo(JSON.stringify(newValue));
            }}
            hideTitle
            otherInfo={userOtherInfo}
          />
        ) : (
          <FieldSkeleton labelWidths={OTHER_LABEL_WIDTHS} />
        )}
      </section>
    </div>
  );
};

export default User;
